import { Base_GameManager } from "./Base_GameManager";
import { getGameData } from "shared";
export class MatchPlay_GameManager extends Base_GameManager {
    constructor(rc, gc, getCurrentScorecard) {
        super(rc, gc);
        this.id = "MatchPlay";
        this.name = "Match Play";
        this.pars = [];
        this.handicaps = [];
        this.totalStrokes = 0;
        // Kept only for interface/backward compatibility. Stateless scoring does not use it.
        this.headerLine2 = "";
        this.isStrokeHole = false;
        this.strokeCounts = [];
        this._rosterIndex = new Map();
        this.getCurrentScorecard =
            getCurrentScorecard ?? (() => ({ gameData: {} }));
        this.header = gc.header;
        this.players = gc.players.map((p) => rc.roster[p.rosterIndex].id);
        this.strokesReceivedPerRound = {};
        for (const id of this.players) {
            this.strokesReceivedPerRound[id] = 0;
        }
    }
    buildScoringInputs(displayScoreType, holeIndex, currentPlayerId) {
        return { displayScoreType, holeIndex, currentPlayerId };
    }
    buildScoringContext(scoringInputs) {
        return { scoreText: "" };
    }
    buildScoreDisplay(displayScoreType, scoringContext) {
        return scoringContext.scoreText;
    }
    /**
     * Stateless score display.
     * All match state is derived from playerScores + gc.players[].strokesPerHole.
     */
    getScore(displayType, holeIndex, playerScores, currentPlayerId) {
        const matchScore = this.getMatchScore(playerScores, holeIndex);
        const formattedCellScore = matchScore ? this.formatForScorecardCell(matchScore) : "";
        switch (displayType) {
            case "scorecard-game-cell":
                return [formattedCellScore];
            case "scorecard-game-header":
                return [this.header, formattedCellScore];
            default:
                return "";
        }
    }
    getSummaryLine(holeIndex, detailed) {
        return "";
    }
    /**
     * Returns one consolidated Match Play score line for all MatchPlay managers.
     *
     * This is the game-type aggregation layer:
     * - each manager instance still owns its own getScore(...) result
     * - this static method filters to matches involving the current player
     * - this static method joins those match scores into one "MP:" line
     */
    static getAllScoresForGameType(displayScoreType, managers, holeIndex, playerScores, currentPlayerId) {
        if (!currentPlayerId)
            return "";
        const matchPlayManagers = managers.filter((gm) => gm instanceof MatchPlay_GameManager);
        const line = matchPlayManagers
            .filter((gm) => gm.includesPlayer(currentPlayerId))
            .map((gm) => gm.getScore(displayScoreType, holeIndex, playerScores, currentPlayerId))
            .filter((line) => typeof line === "string" && line !== "")
            .join("  ");
        return line ? `MP: ${line}` : "";
    }
    /**
     * Returns structured Match Play summaries for the AV / Contract pipeline.
     *
     * The existing getAllScoresForGameType(...) string-producing method remains
     * unchanged. This method returns one summary object for every
     * MatchPlay_GameManager instance, in manager order.
     */
    static getGameSummaries(managers, holeIndex, playerScores) {
        const matchPlayManagers = managers.filter((gm) => gm instanceof MatchPlay_GameManager);
        const matches = matchPlayManagers.map((gm) => gm.getGameSummary(holeIndex, playerScores));
        const summary = {
            holeNumber: holeIndex + 1,
            useHandicaps: matchPlayManagers[0]?.isUsingHandicaps() ?? false,
            matches,
        };
        // console.log(
        //   "MatchPlay_GameManager.getGameSummaries",
        //   summary
        // );
        return summary;
    }
    static getAvGameSummary(managers, holeIndex, playerScores) {
        return {
            matchPlay: {
                manifest: {},
                data: this.getGameSummaries(managers, holeIndex, playerScores),
            },
        };
    }
    /**
     * Builds the structured summary for this Match Play game.
     *
     * getMatchScore(...) is intentionally display-oriented and returns undefined
     * for an open match when the requested hole is not yet fully scored.
     * For the structured pipeline we still need one summary per manager, so this
     * walks backward to the latest fully-scored state. Before any hole is scored,
     * the match is represented as all square with the full round remaining.
     */
    getGameSummary(holeIndex, playerScores) {
        let matchScore = this.getMatchScore(playerScores, holeIndex);
        if (!matchScore) {
            for (let i = holeIndex - 1; i >= 0; i--) {
                matchScore = this.getMatchScore(playerScores, i);
                if (matchScore) {
                    break;
                }
            }
        }
        const [leftPlayer, rightPlayer] = this.gc.players;
        if (!leftPlayer || !rightPlayer) {
            throw new Error("MatchPlay game requires exactly two players.");
        }
        if (!matchScore) {
            return {
                leftPlayerName: leftPlayer.nickName,
                status: "AS",
                rightPlayerName: rightPlayer.nickName,
            };
        }
        const leftMatchPlayer = matchScore.players[0];
        return {
            leftPlayerName: leftPlayer.nickName,
            status: this.formatGameSummaryStatus(leftMatchPlayer.holesAhead, matchScore.holesRemaining),
            rightPlayerName: rightPlayer.nickName,
        };
    }
    formatGameSummaryStatus(holesAhead, holesRemaining) {
        if (holesAhead === 0) {
            return "AS";
        }
        const amount = Math.abs(holesAhead);
        if (amount > holesRemaining) {
            return holesAhead > 0
                ? `${amount} & ${holesRemaining} UP`
                : `${amount} & ${holesRemaining} DN`;
        }
        if (amount === holesRemaining) {
            return holesAhead > 0
                ? "DORMIE UP"
                : "DORMIE DN";
        }
        return holesAhead > 0
            ? `${amount} UP`
            : `${amount} DN`;
    }
    formatMatchScoreForPlayer(matchScore, currentPlayerId, displayType) {
        const currentPlayer = matchScore.players.find((p) => p.playerId === currentPlayerId);
        const opponent = matchScore.players.find((p) => p.playerId !== currentPlayerId);
        if (!currentPlayer || !opponent)
            return "";
        const amount = Math.abs(currentPlayer.holesAhead);
        const holesRemaining = matchScore.holesRemaining;
        const isTied = currentPlayer.holesAhead === 0;
        const currentPlayerIsWinning = currentPlayer.holesAhead > 0;
        const matchIsDormie = amount === holesRemaining;
        const matchIsFinal = amount > holesRemaining;
        if (isTied) {
            return `${this.getDisplayCode(opponent.playerId)}as`;
        }
        if (matchIsFinal) {
            return `${currentPlayerIsWinning ? "W" : "L"}${amount}&${holesRemaining}${this.getDisplayCode(opponent.playerId)}`;
        }
        if (matchIsDormie) {
            return `Dormie ${currentPlayerIsWinning ? "↑" : "↓"}${this.getDisplayCode(opponent.playerId)} `;
        }
        if (currentPlayerIsWinning) {
            return `${amount}↑${this.getDisplayCode(opponent.playerId)}`;
        }
        return `${amount}↓${this.getDisplayCode(opponent.playerId)}`;
    }
    /**
     * Returns the match state after holeIndex.
     *
     * Rules:
     * - Only count holes where both match players have gross scores.
     * - Net score = gross - strokes received on that hole.
     * - The first player is the positive side of the holesAhead calculation.
     * - Holes remaining is based on the requested hole index.
     * - Once the match is closed, the final closed score is returned for later holes.
     * - If the match is not closed, the requested hole must have both scores entered.
     */
    getMatchScore(playerScores, holeIndex) {
        if (!playerScores)
            return undefined;
        if (this.gc.players.length < 2)
            return undefined;
        const [playerA, playerB] = this.gc.players;
        const playerAId = playerA.id;
        const playerBId = playerB.id;
        const playerAScore = playerScores.find((p) => p.playerId === playerAId);
        const playerBScore = playerScores.find((p) => p.playerId === playerBId);
        if (!playerAScore || !playerBScore)
            return undefined;
        const totalHoles = this.rc.holesInRound ?? 18;
        const useHandicaps = this.isUsingHandicaps();
        let playerAHolesAhead = 0;
        let closedMatchScore;
        let closedOnHoleIndex;
        for (let i = 0; i <= holeIndex; i++) {
            const playerAGross = playerAScore.gross[i];
            const playerBGross = playerBScore.gross[i];
            // Skipped holes do not affect the match score.
            if (!playerAGross || !playerBGross)
                continue;
            const playerAStrokes = this.getPlayerStrokesOnHole(playerAId, i);
            const playerBStrokes = this.getPlayerStrokesOnHole(playerBId, i);
            const playerANet = playerAGross - playerAStrokes;
            const playerBNet = playerBGross - playerBStrokes;
            if (playerANet < playerBNet) {
                playerAHolesAhead += 1;
            }
            else if (playerBNet < playerANet) {
                playerAHolesAhead -= 1;
            }
            const holesRemaining = Math.max(0, totalHoles - i - 1);
            if (!closedMatchScore && Math.abs(playerAHolesAhead) > holesRemaining) {
                closedMatchScore = this.buildMatchScore(playerAId, playerBId, playerAHolesAhead, holesRemaining);
                closedOnHoleIndex = i;
            }
        }
        if (closedMatchScore) {
            return {
                ...closedMatchScore,
                suppressScorecardDisplay: closedOnHoleIndex !== holeIndex,
            };
        }
        const currentPlayerAGross = playerAScore.gross[holeIndex];
        const currentPlayerBGross = playerBScore.gross[holeIndex];
        // Like StrokePlay cells: an open match is blank unless this hole is scored.
        if (!currentPlayerAGross || !currentPlayerBGross)
            return undefined;
        return this.buildMatchScore(playerAId, playerBId, playerAHolesAhead, Math.max(0, totalHoles - holeIndex - 1));
    }
    buildMatchScore(playerAId, playerBId, playerAHolesAhead, holesRemaining) {
        return {
            players: [
                { playerId: playerAId, holesAhead: playerAHolesAhead },
                { playerId: playerBId, holesAhead: -playerAHolesAhead },
            ],
            holesRemaining,
        };
    }
    getDisplayCode(playerId) {
        return this.gc.players.find(p => p.id === playerId)?.displayCode ?? playerId;
    }
    formatForScorecardCell(matchScore) {
        if (matchScore.suppressScorecardDisplay) {
            return "";
        }
        const leader = matchScore.players.find((p) => p.holesAhead > 0);
        if (!leader) {
            return matchScore.holesRemaining === 0 ? "All Square" : "AS";
        }
        const amount = leader.holesAhead;
        const holesRemaining = matchScore.holesRemaining;
        if (amount > holesRemaining) {
            return `${this.getDisplayCode(leader.playerId)} ${amount} & ${holesRemaining}`;
        }
        if (amount === holesRemaining) {
            return `${this.getDisplayCode(leader.playerId)} Dormie`;
        }
        return `${this.getDisplayCode(leader.playerId)} ${amount} UP`;
    }
    isUsingHandicaps() {
        const scorecard = this.getCurrentScorecard();
        const matchPlayData = getGameData(scorecard, "Match Play");
        const useHandicaps = matchPlayData.useHandicaps ?? false;
        return useHandicaps;
    }
    getPlayerStrokesOnHole(playerId, holeIndex) {
        if (!this.isUsingHandicaps())
            return 0;
        const player = this.gc.players.find((p) => p.id === playerId);
        return player?.strokesPerHole?.[holeIndex] ?? 0;
    }
    reset() {
        this.headerLine2 = "";
    }
    /**
     * Backward-compatible helper used by scorecard highlighting.
     * Returns the largest stroke count given on this hole by either match player.
     */
    getStrokesOnHole(holeIndex) {
        if (!this.isUsingHandicaps())
            return 0;
        let maxStrokes = 0;
        for (const p of this.gc.players) {
            const strokes = p.strokesPerHole?.[holeIndex] ?? 0;
            if (strokes > maxStrokes)
                maxStrokes = strokes;
        }
        return maxStrokes;
    }
    getGameContext() {
        return this.gc;
    }
    getParticipants() {
        return this.players;
    }
    includesPlayer(id) {
        return this.players.includes(id);
    }
    getDisplayValue(holeIndex) {
        return "";
    }
    getHeaderLine1() {
        return this.header;
    }
    getHeaderLine2() {
        return this.headerLine2;
    }
    /**
     * Stateless scoring path no longer uses onHoleScore.
     * Kept only so older callers do not break while ScorecardBuilder is migrated to getScore().
     */
    onHoleScore(fc) {
        return "";
    }
    static CreateGameManagers(rc, scorecardData, getCurrentScorecard) {
        const roster = rc.roster ?? [];
        const managers = [];
        const scorecardProvider = getCurrentScorecard ?? (() => scorecardData);
        for (let i = 0; i < roster.length; i++) {
            for (let j = i + 1; j < roster.length; j++) {
                const entryA = roster[i];
                const entryB = roster[j];
                const hdcpA = entryA.handicap;
                const hdcpB = entryB.handicap;
                const diff = Math.min(36, Math.abs(hdcpA - hdcpB));
                let header;
                if (diff === 0) {
                    header = `${entryA.displayCode}-${entryB.displayCode}`;
                }
                else {
                    const giverEntry = hdcpA < hdcpB ? entryA : entryB;
                    const receiverEntry = giverEntry === entryA ? entryB : entryA;
                    header = `${giverEntry.displayCode}-${receiverEntry.displayCode}`;
                }
                const players = MatchPlay_GameManager.buildPlayersForPair(rc, i, j);
                const gc = {
                    players,
                    header,
                    // Kept for compatibility, but stateless MatchPlay does not read/write it.
                    scoreDisplaysByHole: Array(rc.holesInRound).fill(null),
                    skinsCarriedForward: 0,
                };
                managers.push(new MatchPlay_GameManager(rc, gc, scorecardProvider));
            }
        }
        return managers;
    }
    static allocateStrokesPerHole(totalStrokes, holes, strokeIndex) {
        const strokesPerHole = new Array(holes).fill(0);
        if (totalStrokes <= 0) {
            return strokesPerHole;
        }
        const si = strokeIndex.slice(0, holes);
        const base = Math.floor(totalStrokes / holes);
        const remainder = totalStrokes % holes;
        for (let h = 0; h < holes; h++) {
            strokesPerHole[h] = base;
        }
        if (remainder > 0) {
            for (let h = 0; h < holes; h++) {
                const siValue = si[h];
                if (typeof siValue === "number" &&
                    siValue >= 1 &&
                    siValue <= remainder) {
                    strokesPerHole[h] += 1;
                }
            }
        }
        return strokesPerHole;
    }
    /**
     * Builds the two match players. The lower-handicap player is placed first
     * so player 1 is the stroke-giving/anchor player when the match is not even.
     */
    static buildPlayersForPair(rc, i, j) {
        const holes = rc.holesInRound;
        const si = rc.handicaps;
        const entryA = rc.roster[i];
        const entryB = rc.roster[j];
        let firstRosterIndex = i;
        let secondRosterIndex = j;
        if (entryA.handicap !== entryB.handicap) {
            firstRosterIndex = entryA.handicap < entryB.handicap ? i : j;
            secondRosterIndex = firstRosterIndex === i ? j : i;
        }
        const firstEntry = rc.roster[firstRosterIndex];
        const secondEntry = rc.roster[secondRosterIndex];
        const diff = Math.abs(firstEntry.handicap - secondEntry.handicap);
        const firstStrokes = firstEntry.handicap > secondEntry.handicap ? diff : 0;
        const secondStrokes = secondEntry.handicap > firstEntry.handicap ? diff : 0;
        const p1 = {
            rosterIndex: firstRosterIndex,
            nickName: firstEntry.nickName,
            displayCode: firstEntry.displayCode,
            id: firstEntry.id,
            strokesReceived: firstStrokes,
            strokesPerHole: MatchPlay_GameManager.allocateStrokesPerHole(firstStrokes, holes, si),
            cumulativeGrossScore: Array(holes).fill(0),
            cumulativeNetScore: Array(holes).fill(0),
        };
        const p2 = {
            rosterIndex: secondRosterIndex,
            nickName: secondEntry.nickName,
            displayCode: secondEntry.displayCode,
            id: secondEntry.id,
            strokesReceived: secondStrokes,
            strokesPerHole: MatchPlay_GameManager.allocateStrokesPerHole(secondStrokes, holes, si),
            cumulativeGrossScore: Array(holes).fill(0),
            cumulativeNetScore: Array(holes).fill(0),
        };
        return [p1, p2];
    }
    getHeaderRender() {
        const [player1, player2] = this.gc.players;
        if (!player1 || !player2) {
            return {
                cssClass: "match-play-header",
                text: this.header,
            };
        }
        const player1Code = this.escapeHtml(player1.displayCode);
        const player2Code = this.escapeHtml(player2.displayCode);
        return {
            cssClass: "match-play-header",
            html: `
      <span class="winner-a-header">${player1Code}</span>
      -
      <span class="winner-b-header">${player2Code}</span>
    `,
        };
    }
    escapeHtml(value) {
        return value
            .replaceAll("&", "&amp;")
            .replaceAll("<", "&lt;")
            .replaceAll(">", "&gt;")
            .replaceAll('"', "&quot;")
            .replaceAll("'", "&#039;");
    }
    getScoreCellRender(holeIndex, playerIndex, playerScores) {
        const cssClass = this.getHoleWinnerCssClass(holeIndex, playerScores);
        return cssClass ? { cssClass } : undefined;
    }
    getHoleWinnerCssClass(holeIndex, playerScores) {
        if (!playerScores)
            return "";
        if (this.gc.players.length < 2)
            return "";
        const [player1, player2] = this.gc.players;
        const player1Score = playerScores.find(p => p.playerId === player1.id);
        const player2Score = playerScores.find(p => p.playerId === player2.id);
        if (!player1Score || !player2Score)
            return "";
        const player1Gross = player1Score.gross[holeIndex];
        const player2Gross = player2Score.gross[holeIndex];
        if (!player1Gross || !player2Gross)
            return "";
        const player1Net = player1Gross - this.getPlayerStrokesOnHole(player1.id, holeIndex);
        const player2Net = player2Gross - this.getPlayerStrokesOnHole(player2.id, holeIndex);
        if (player1Net < player2Net) {
            return "winner-a-dot";
        }
        if (player2Net < player1Net) {
            return "winner-b-dot";
        }
        return "";
    }
}
//# sourceMappingURL=MatchPlay_GameManager.js.map